import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Layout } from './components/Layout';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import FamiliesPage from './pages/FamiliesPage';
import FamilyViewPage from './pages/FamilyViewPage';
import MembersPage from './pages/MembersPage';
import NonTaggedPage from './pages/NonTaggedPage';
import MohallaSectorManagement from './pages/MohallaSectorManagement';
import LeadershipPage from './pages/LeadershipPage';
import FMBPage from './pages/FMBPage';
import ListsPage from './pages/ListsPage';
import AuditPage from './pages/AuditPage';
import MessagingPage from './pages/MessagingPage';
import UsersPage from './pages/UsersPage';
import DataMaintenancePage from './pages/DataMaintenancePage';
import LiveStreamingPage from './pages/LiveStreamingPage';
import LiveLoginPage from './live/LiveLoginPage';
import LiveDashboardPage from './live/LiveDashboardPage';
import LiveWatchPage from './live/LiveWatchPage';
import LiveTvLoginPage from './live/LiveTvLoginPage';
import LiveChangePasswordPage from './live/LiveChangePasswordPage';
import LiveQrSuccessPage from './live/LiveQrSuccessPage';
import LiveEntryPage from './live/LiveEntryPage';
import DrilldownPage from './pages/DrilldownPage';
import EventsPage from './pages/EventsPage';
import EidZabhiyatPage from './pages/EidZabhiyatPage';
import TolobaPage from './pages/TolobaPage';
import MyFamilyPage from './pages/MyFamilyPage';
import TokenEventsPage from './pages/TokenEventsPage';
import TokenEventDetailPage from './pages/TokenEventDetailPage';
import RamzanSeatPage from './pages/RamzanSeatPage';
import SeatManagementPage from './pages/SeatManagementPage';
import UmoorSehatVaccinationPage from './pages/UmoorSehatVaccinationPage';
import HofSeatPortal from './pages/HofSeatPortal';
import FormsPage from './pages/FormsPage';
import FormBuilderPage from './pages/FormBuilderPage';
import FormResultsPage from './pages/FormResultsPage';
import PublicFormPage from './pages/PublicFormPage';
import WajebaatPage from './pages/WajebaatPage';
import AttendancePage from './pages/AttendancePage';
import PublicAttendancePage from './pages/PublicAttendancePage';
import VideosPage from './pages/VideosPage';
import PublicVideoPage from './pages/PublicVideoPage';
import HeadLadiesPage from './pages/HeadLadiesPage';
import SHNDPage from './pages/SHNDPage';
import SHNDFormPage from './pages/SHNDFormPage';
import PublicSHNDPage from './pages/PublicSHNDPage';
import PublicEventRegisterPage from './pages/PublicEventRegisterPage';
import './App.css';

function PrivateRoute({ children, allowedRoles, requireEventAccess, blockForRoles }) {
  const { token, loading, user, canViewDashboard, canViewAuditLogs, isMasool, isMusaid, isFMBViewer, isTolobaCollector, canViewFMB } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" />;
  }
  
  // Block specific roles from certain routes
  if (blockForRoles && blockForRoles.includes(user?.role)) {
    return <Navigate to="/toloba" />;
  }
  
  // Restricted view roles (Masool/Musaid/FMB Viewer) - limited access
  const isRestrictedRole = isMasool || isMusaid || isFMBViewer;
  
  // Toloba collector - redirect to Toloba if trying to access blocked pages
  if (isTolobaCollector) {
    // Allow only specific routes for toloba_collector
    const allowedPaths = ['/toloba', '/mohalla-sector-management', '/drilldown'];
    const currentPath = window.location.pathname;
    if (!allowedPaths.some(p => currentPath.startsWith(p))) {
      return <Navigate to="/toloba" />;
    }
  }
  
  // Mumineen role restrictions
  if (user?.role === 'mumineen') {
    const hasEventAccess = user?.event_access && user.event_access.length > 0;
    const hasLeadershipRole = user?.leadership_role;
    
    // If requireEventAccess is specified, check if user has it
    if (requireEventAccess && !hasEventAccess && !hasLeadershipRole) {
      return <Navigate to="/my-family" />;
    }
    
    // If allowedRoles is specified, check if mumineen is allowed
    if (allowedRoles && !allowedRoles.includes('mumineen')) {
      // Only block if they don't have event access
      if (!hasEventAccess && !hasLeadershipRole) {
        return <Navigate to="/my-family" />;
      }
    }
  }
  
  return <Layout>{children}</Layout>;
}

// Route guard for Audit Logs - Super Admin only
function AuditRoute({ children }) {
  const { token, loading, canViewAuditLogs } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" />;
  }
  
  // Only Super Admin can view audit logs
  if (!canViewAuditLogs) {
    return <Navigate to="/" />;
  }
  
  return <Layout>{children}</Layout>;
}

// Route guard for admin-only pages (Seat Management admin panel)
function AdminOnlyRoute({ children }) {
  const { token, loading, isAdmin } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!token) return <Navigate to="/login" />;
  if (!isAdmin) return <Navigate to="/" />;
  return <Layout>{children}</Layout>;
}

// Route guard for Vaccination Drive — admin OR vaccination_coordinator
function VaccinationRoute({ children }) {
  const { token, loading, isAdmin, user } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!token) return <Navigate to="/login" />;
  const ok = isAdmin || user?.role === 'vaccination_coordinator'
    || (Array.isArray(user?.roles) && user.roles.includes('vaccination_coordinator'));
  if (!ok) return <Navigate to="/" />;
  return <Layout>{children}</Layout>;
}

// Route guard for Live Streaming — admin OR live_stream_manager
function LiveStreamRoute({ children }) {
  const { token, loading, isAdmin, user } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!token) return <Navigate to="/login" />;
  const ok = isAdmin || user?.role === 'live_stream_manager'
    || (Array.isArray(user?.roles) && user.roles.includes('live_stream_manager'));
  if (!ok) return <Navigate to="/" />;
  return <Layout>{children}</Layout>;
}

// Route guard for Dashboard - Admin roles only (not Masool/Musaid/FMB Viewer)
function DashboardRoute({ children }) {
  const { token, loading, canViewDashboard } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" />;
  }
  
  // Restricted roles cannot access dashboard
  if (!canViewDashboard) {
    return <Navigate to="/fmb" />;
  }
  
  return <Layout>{children}</Layout>;
}

// Route for Mumineen users - shows their family
function MumineenRoute({ children }) {
  const { token, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" />;
  }
  
  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
  const { token, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  return token ? <Navigate to="/" /> : children;
}

// SHND index route: show public form for guests, admin dashboard for logged-in admin/shnd users
function SHNDIndexRoute() {
  const { token, loading, user } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!token) {
    return <PublicSHNDPage />;
  }
  const roles = Array.isArray(user?.roles) ? user.roles : (user?.role ? [user.role] : []);
  const isAdmin = roles.includes('admin') || roles.includes('super_admin') || roles.includes('shnd');
  if (isAdmin) {
    return (
      <Layout>
        <SHNDPage />
      </Layout>
    );
  }
  return <PublicSHNDPage />;
}

// Smart home route - redirects based on user role
function HomeRoute() {
  const { user, canViewDashboard, canViewFMB, isMasool, isMusaid, isFMBViewer, isTolobaCollector, tolobaAccess, eventAccess } = useAuth();
  
  // List manager - go to Lists page
  if (user?.role === 'list_manager') {
    return <Navigate to="/lists" replace />;
  }
  
  // Mumineen users with Toloba access - go to Toloba page
  if (user?.role === 'mumineen' && tolobaAccess && tolobaAccess.length > 0) {
    return <Navigate to="/toloba" replace />;
  }
  
  // Mumineen users with Event access - go to Events page
  if (user?.role === 'mumineen' && eventAccess && eventAccess.length > 0) {
    return <Navigate to="/events" replace />;
  }
  
  // Mumineen users (regular members) - always go to My Family page
  if (user?.role === 'mumineen') {
    return <Navigate to="/my-family" replace />;
  }
  
  // Seat management - go to Ramzan Seats page
  if (user?.role === 'seat_management') {
    return <Navigate to="/ramzan-seats" replace />;
  }
  
  // Token manager - go to Token Events page
  if (user?.role === 'token_manager') {
    return <Navigate to="/token-events" replace />;
  }
  
  // Live Stream Manager - go to Live Streaming page
  if (user?.role === 'live_stream_manager' || (Array.isArray(user?.roles) && user.roles.includes('live_stream_manager'))) {
    return <Navigate to="/live-streaming" replace />;
  }
  
  // List manager - go to Lists page
  if (user?.role === 'list_manager') {
    return <Navigate to="/lists" replace />;
  }
  
  // Toloba collector - go to Toloba page
  if (isTolobaCollector) {
    return <Navigate to="/toloba" replace />;
  }
  
  // Masool/Musaid - go to Families page (they have no FMB access now)
  if (isMasool || isMusaid) {
    return <Navigate to="/families" replace />;
  }
  
  // FMB Viewer - go to FMB page
  if (isFMBViewer && canViewFMB) {
    return <Navigate to="/fmb" replace />;
  }
  
  // Users without dashboard access but with FMB access - go to FMB
  if (!canViewDashboard && canViewFMB) {
    return <Navigate to="/fmb" replace />;
  }
  
  // Users without dashboard access and no FMB - go to Families
  if (!canViewDashboard) {
    return <Navigate to="/families" replace />;
  }
  
  // Admin/Super Admin - go to Dashboard
  return <DashboardPage />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/seat" element={<HofSeatPortal />} />
      <Route path="/" element={<PrivateRoute><HomeRoute /></PrivateRoute>} />
      <Route path="/my-family" element={<MumineenRoute><MyFamilyPage /></MumineenRoute>} />
      <Route path="/families" element={<PrivateRoute><FamiliesPage /></PrivateRoute>} />
      <Route path="/family/:hofId" element={<PrivateRoute><FamilyViewPage /></PrivateRoute>} />
      <Route path="/members" element={<PrivateRoute><MembersPage /></PrivateRoute>} />
      <Route path="/non-tagged" element={<PrivateRoute><NonTaggedPage /></PrivateRoute>} />
      <Route path="/mohalla-sector-management" element={<PrivateRoute><MohallaSectorManagement /></PrivateRoute>} />
      <Route path="/leadership" element={<PrivateRoute><LeadershipPage /></PrivateRoute>} />
      <Route path="/fmb" element={<PrivateRoute><FMBPage /></PrivateRoute>} />
      <Route path="/events" element={<PrivateRoute><EventsPage /></PrivateRoute>} />
      <Route path="/eid-zabhiyat" element={<PrivateRoute><EidZabhiyatPage /></PrivateRoute>} />
      <Route path="/token-events" element={<PrivateRoute><TokenEventsPage /></PrivateRoute>} />
      <Route path="/token-events/:eventId" element={<PrivateRoute><TokenEventDetailPage /></PrivateRoute>} />
      <Route path="/ramzan-seats" element={<PrivateRoute><RamzanSeatPage /></PrivateRoute>} />
      <Route path="/seat-management" element={<AdminOnlyRoute><SeatManagementPage /></AdminOnlyRoute>} />
      <Route path="/umoor-sehat/vaccination" element={<VaccinationRoute><UmoorSehatVaccinationPage /></VaccinationRoute>} />
      <Route path="/wajebaat" element={<PrivateRoute><WajebaatPage /></PrivateRoute>} />
      <Route path="/toloba" element={<PrivateRoute><TolobaPage /></PrivateRoute>} />
      <Route path="/lists" element={<PrivateRoute><ListsPage /></PrivateRoute>} />
      <Route path="/audit" element={<AuditRoute><AuditPage /></AuditRoute>} />
      <Route path="/messaging" element={<PrivateRoute><MessagingPage /></PrivateRoute>} />
      <Route path="/users" element={<PrivateRoute><UsersPage /></PrivateRoute>} />
      <Route path="/data-maintenance" element={<AdminOnlyRoute><DataMaintenancePage /></AdminOnlyRoute>} />
      <Route path="/live-streaming" element={<LiveStreamRoute><LiveStreamingPage /></LiveStreamRoute>} />

      {/* Live Streaming viewer routes (separate auth, no main SMNGP layout) */}
      <Route path="/live" element={<LiveEntryPage />} />
      <Route path="/live/login" element={<LiveLoginPage />} />
      <Route path="/live/dashboard" element={<LiveDashboardPage />} />
      <Route path="/live/watch/:streamId" element={<LiveWatchPage />} />
      <Route path="/live/tv" element={<LiveTvLoginPage />} />
      <Route path="/live/change-password" element={<LiveChangePasswordPage />} />
      <Route path="/live/qr-success" element={<LiveQrSuccessPage />} />
      <Route path="/drilldown/:type/:value" element={<PrivateRoute><DrilldownPage /></PrivateRoute>} />
      <Route path="/forms" element={<PrivateRoute><FormsPage /></PrivateRoute>} />
      <Route path="/form-builder/:formId" element={<PrivateRoute><FormBuilderPage /></PrivateRoute>} />
      <Route path="/form-results/:formId" element={<PrivateRoute><FormResultsPage /></PrivateRoute>} />
      {/* Public form submission page - no auth required */}
      <Route path="/public-form/:formId" element={<PublicFormPage />} />
      {/* Attendance routes */}
      <Route path="/attendance-manage" element={<PrivateRoute><AttendancePage /></PrivateRoute>} />
      {/* Public attendance page - no auth required */}
      <Route path="/attendance/:publicCode" element={<PublicAttendancePage />} />
      
      {/* Public SHND form - no auth required */}
      <Route path="/shnd/public" element={<PublicSHNDPage />} />
      {/* Public Event Registration - no auth required */}
      <Route path="/register/:slug" element={<PublicEventRegisterPage />} />
      {/* Video routes */}
      <Route path="/videos" element={<PrivateRoute><VideosPage /></PrivateRoute>} />
      {/* /shnd: public form for guests, admin dashboard for authenticated users */}
      <Route path="/shnd" element={<SHNDIndexRoute />} />
      <Route path="/shnd/:appId" element={<PrivateRoute><SHNDFormPage /></PrivateRoute>} />
      {/* Public video page - no auth required */}
      <Route path="/video/:publicCode" element={<PublicVideoPage />} />
      {/* Head Ladies page */}
      <Route path="/head-ladies" element={<PrivateRoute><HeadLadiesPage /></PrivateRoute>} />
      {/* Redirect old routes to new management page */}
      <Route path="/mohallas" element={<Navigate to="/mohalla-sector-management" />} />
      <Route path="/sectors" element={<Navigate to="/mohalla-sector-management" />} />
      <Route path="/mohalla-list" element={<Navigate to="/mohalla-sector-management" />} />
      <Route path="/sector-list" element={<Navigate to="/mohalla-sector-management" />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
